import { Spin } from 'antd';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  PlanetMosaicItem,
  PlanetMosaicType,
  RemoteImageAlert_GetListPlanetMosaic_Request,
  remote_GetListPlanetMosaic
} from '~/common/typescript/apis/RemoteImageAlertAPIs';
import { useAtom, useSetAtom } from 'jotai';
import { planetItemsLEFTAtom, planetItemsRIGHTAtom, planetFilterTypeAtom, planetFilterYearAtom, planetFilterMonthAtom, planetFilterQuarterAtom } from './store';
import PlanetItem from './PlanetItem';
import PlanetFilterPanel from './PlanetFilterPanel';
import styled from 'styled-components';
import { satelliteRecordCountAtom, satelliteFilterTriggerAtom } from '../../store';

/**
 * A finder component for searching and displaying Planet Labs satellite imagery.
 * It provides filters for date, cloud cover, and instrument,
 * and displays results in a list.
 */
const PlanetFinder: React.FC = () => {
  const { t } = useTranslation();

  // Shared filter state atoms
  const [type, setType] = useAtom(planetFilterTypeAtom);
  const [year, setYear] = useAtom(planetFilterYearAtom);
  const [month, setMonth] = useAtom(planetFilterMonthAtom);
  const [quarter, setQuarter] = useAtom(planetFilterQuarterAtom);

  // State for search results and loading
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<PlanetMosaicItem[]>([]);

  // Atoms for storing items for left and right map
  const [planetItemsLeft, setPlanetItemsLeft] = useAtom(planetItemsLEFTAtom);
  const [planetItemsRight, setPlanetItemsRight] = useAtom(planetItemsRIGHTAtom);

  const [filterVisible, setFilterVisible] = useState(false);

  const handleSearch = async () => {
    setLoading(true);

    try {
      const request: RemoteImageAlert_GetListPlanetMosaic_Request = {
        type: type,
        year: year,
      };

      if (type === PlanetMosaicType.QUARTERLY) {
        request.quarter = quarter;
      } else if (type === PlanetMosaicType.MONTHLY) {
        if (month !== null) {
          request.month = month;
        }
      }

      const response = await remote_GetListPlanetMosaic(request);
      setResults(response);
      setFilterVisible(false); // Close the filter after search
    } catch (error) {
      console.error('Error searching Planet imagery:', error);
      toast.error('Lỗi khi tìm kiếm ảnh vệ tinh Planet', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to add image to the left map
  const handleAddToLeftMap = (image: PlanetMosaicItem) => {
    // Check if the item already exists in the left map
    const exists = planetItemsLeft.some(item => item.id === image.id);
    if (!exists) {
      setPlanetItemsLeft([...planetItemsLeft, image]);
      toast.success('Đã thêm ảnh vào bản đồ bên trái', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    } else {
      toast.warning('Ảnh đã tồn tại trong bản đồ bên trái', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    }
  };

  // Function to add image to the right map
  const handleAddToRightMap = (image: PlanetMosaicItem) => {
    // Check if the item already exists in the right map
    const exists = planetItemsRight.some(item => item.id === image.id);
    if (!exists) {
      setPlanetItemsRight([...planetItemsRight, image]);
      toast.success('Đã thêm ảnh vào bản đồ bên phải', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    } else {
      toast.warning('Ảnh đã tồn tại trong bản đồ bên phải', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    }
  };

  // Function to add image to QGIS
  const handleAddToQGIS = (image: PlanetMosaicItem) => {
    toast.info('Chức năng QGIS chưa được triển khai trong ví dụ này', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });
  };

  const setRecordCount = useSetAtom(satelliteRecordCountAtom);
  const setFilterTrigger = useSetAtom(satelliteFilterTriggerAtom);

  // Update record count when the list changes
  useEffect(() => {
    setRecordCount(results.length);
  }, [results, setRecordCount]);

  // Register the filter trigger function
  useEffect(() => {
    setFilterTrigger(() => {
      setFilterVisible(true);
    });
    return () => {
      setFilterTrigger(null);
    };
  }, [setFilterTrigger]);

  // Auto-fetch data on mount
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <UI_Container>
        {/* Results Panel */}
        <h4>{t('planet_finder.results', 'Results')}</h4>
        <Spin spinning={loading}>
          <UI_ResultsGrid>
            {results.map((item) => (
              <PlanetItem
                key={item.id}
                item={item}
                onAddToLeft={handleAddToLeftMap}
                onAddToRight={handleAddToRightMap}
                onViewOnQGIS={handleAddToQGIS}
              />
            ))}
          </UI_ResultsGrid>
        </Spin>
      </UI_Container>

      {/* Filter popup */}
      <PlanetFilterPanel
        visible={filterVisible}
        onClose={() => setFilterVisible(false)}
        type={type}
        setType={setType}
        year={year}
        setYear={setYear}
        month={month}
        setMonth={setMonth}
        quarter={quarter}
        setQuarter={setQuarter}
        onSearch={handleSearch}
      />
    </>
  );
};

export default PlanetFinder;

const UI_Container = styled.div`
  padding: 8px;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const UI_ResultsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-start;
`;